import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Monitor, Users, FileText, Download, Search, ChevronDown, ChevronUp,
  Rocket, Target, Pencil, GraduationCap, ArrowLeft, ToggleLeft, ToggleRight,
  Info, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuiviElearning = ({ sessionId, sessionTitle = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('alpha_asc');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/suivi-elearning`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchDetail = async (learnerEmail, learnerName) => {
    setSelectedLearner({ email: learnerEmail, name: learnerName });
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/suivi-elearning/${encodeURIComponent(learnerEmail)}`, { credentials: 'include' });
      if (res.ok) setDetailData(await res.json());
    } catch (e) { /* silent */ }
    setDetailLoading(false);
  };

  const goBack = () => { setSelectedLearner(null); setDetailData(null); };

  // Filtered and sorted learners
  const learners = data?.learners || [];
  const filtered = learners.filter(l => {
    const matchSearch = l.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchActive = !showActiveOnly || l.status === 'actif';
    return matchSearch && matchActive;
  }).sort((a, b) => {
    if (sortBy === 'alpha_asc') return a.name.localeCompare(b.name);
    if (sortBy === 'alpha_desc') return b.name.localeCompare(a.name);
    if (sortBy === 'progress_desc') return b.advancement - a.advancement;
    if (sortBy === 'score_desc') return b.score - a.score;
    return 0;
  });

  // Detail view for a specific learner
  if (selectedLearner) {
    return (
      <div className="space-y-6 mt-4" data-testid="suivi-elearning-detail">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <button onClick={goBack} className="text-violet-600 hover:text-violet-800 flex items-center gap-1" data-testid="btn-back-to-list">
            <ArrowLeft className="h-4 w-4" />{sessionTitle || 'Session'}
          </button>
          <span className="text-slate-400">&gt;</span>
          <span className="text-slate-500">Suivi e-learning</span>
          <span className="text-slate-400">&gt;</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">{selectedLearner.name}</span>
        </div>

        <h3 className="text-lg font-bold text-slate-900 dark:text-white">
          Suivi e-learning :
          <Select value={selectedLearner.email} onValueChange={v => {
            const l = learners.find(ll => ll.email === v);
            if (l) fetchDetail(l.email, l.name);
          }}>
            <SelectTrigger className="inline-flex h-8 w-auto ml-2 text-sm border-0 bg-transparent shadow-none">
              <SelectValue>{selectedLearner.name}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {learners.map(l => <SelectItem key={l.email} value={l.email}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </h3>

        {detailLoading ? (
          <div className="py-12 text-center text-sm text-slate-400">Chargement...</div>
        ) : detailData ? (
          <>
            {/* Overview cards */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl p-6 text-white" data-testid="detail-overview">
              <div className="grid grid-cols-3 gap-8">
                <div className="flex items-center gap-3">
                  <Rocket className="h-6 w-6 opacity-80" />
                  <div>
                    <p className="text-3xl font-bold">{detailData.advancement}%</p>
                    <p className="text-sm opacity-80">Avancement général</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Target className="h-6 w-6 opacity-80" />
                  <div>
                    <p className="text-3xl font-bold">{detailData.score}%</p>
                    <p className="text-sm opacity-80">Score</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Pencil className="h-6 w-6 opacity-80" />
                  <div>
                    <p className="text-3xl font-bold">{detailData.to_grade}</p>
                    <p className="text-sm opacity-80">Réalisations à noter</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sequences breakdown */}
            <div className="space-y-3" data-testid="detail-sequences">
              {(detailData.sequences || []).map((seq, i) => (
                <div key={seq.seq_id || i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-violet-200 transition-colors"
                  data-testid={`detail-seq-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                      <GraduationCap className="h-6 w-6 text-violet-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{seq.title}</p>
                      <div className="flex items-center gap-6 mt-2">
                        {/* Progress */}
                        <div className="flex items-center gap-2 min-w-[200px]">
                          <span className="text-xs text-slate-400">Avancement global</span>
                          <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${seq.advancement}%` }} />
                          </div>
                          <span className="text-xs font-medium text-slate-600">{seq.completed_activities} / {seq.total_activities}</span>
                        </div>
                        {/* Activities */}
                        <div className="flex items-center gap-1.5">
                          <BookOpen className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs text-slate-500">Activités terminées</span>
                          <span className="text-xs font-medium text-slate-700">{seq.completed_activities} / {seq.total_activities}</span>
                        </div>
                        {/* To correct */}
                        <div className="flex items-center gap-1.5">
                          <Info className="h-3.5 w-3.5 text-amber-500" />
                          <span className="text-xs text-slate-500">À corriger</span>
                          <span className="text-xs font-medium text-slate-700">{seq.to_correct}</span>
                        </div>
                        {/* Score */}
                        <Badge variant="outline" className={`text-xs ${seq.score > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                          Score: {seq.score > 0 ? `${seq.score} / 100` : '- / 100'}
                        </Badge>
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              ))}
              {(detailData.sequences || []).length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">
                  Aucune séquence e-learning configurée.
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="py-12 text-center text-sm text-slate-400">Aucune donnée disponible.</div>
        )}
      </div>
    );
  }

  // Overview list view
  return (
    <div className="space-y-6 mt-4" data-testid="suivi-elearning-content">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="elearning-summary-cards">
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 200 200" className="w-full h-full"><path d="M 0 80 Q 50 40 100 80 T 200 80 V 200 H 0 Z" fill="white" /></svg>
          </div>
          <div className="relative">
            <h4 className="text-sm font-medium opacity-80 mb-3">Apprenants</h4>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 opacity-80" />
                <span className="text-2xl font-bold">{data?.active_learners || 0}</span>
                <span className="text-sm opacity-80">apprenants actifs</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 opacity-60" />
                <span className="text-2xl font-bold">{data?.inactive_learners || 0}</span>
                <span className="text-sm opacity-80">apprenants inactifs</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 200 200" className="w-full h-full"><path d="M 0 80 Q 50 40 100 80 T 200 80 V 200 H 0 Z" fill="white" /></svg>
          </div>
          <div className="relative">
            <h4 className="text-sm font-medium opacity-80 mb-3">Réalisations</h4>
            <div className="flex items-center gap-6">
              <div>
                <span className="text-2xl font-bold">{data?.total_realisations || 0}</span>
                <span className="text-sm opacity-80 ml-2">Réalisations reçues</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-red-500/30 flex items-center justify-center">
                  <span className="text-xs font-bold">{data?.realisations_to_correct || 0}</span>
                </div>
                <span className="text-sm opacity-80">Réalisations à corriger</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Learners list header */}
      <div className="flex flex-wrap items-center gap-4">
        <h3 className="text-base font-bold text-slate-900 dark:text-white">
          Mes {data?.total_learners || 0} apprenants sélectionnés
        </h3>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="elearning-filters">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input className="h-9 pl-9 w-56 text-xs" placeholder="Rechercher un apprenant..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-elearning" />
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Tri</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-9 w-48 text-xs" data-testid="sort-elearning">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alpha_asc">Alphabétique A &gt; Z</SelectItem>
              <SelectItem value="alpha_desc">Alphabétique Z &gt; A</SelectItem>
              <SelectItem value="progress_desc">Avancement décroissant</SelectItem>
              <SelectItem value="score_desc">Score décroissant</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <button className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer"
          onClick={() => setShowActiveOnly(!showActiveOnly)} data-testid="toggle-active-only">
          {showActiveOnly ? <ToggleRight className="h-5 w-5 text-violet-500" /> : <ToggleLeft className="h-5 w-5 text-slate-300" />}
          Uniquement les actifs
        </button>
        <div className="flex-1" />
        <Button variant="outline" className="text-xs gap-1.5" data-testid="btn-export-elearning">
          <Download className="h-3.5 w-3.5" />Exporter la progression et les scores
        </Button>
      </div>

      {/* Learners list */}
      <div className="space-y-3" data-testid="elearning-learners-list">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="no-elearning-learners">
            <Monitor className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p>Aucun apprenant trouvé.</p>
          </div>
        ) : filtered.map((learner, i) => (
          <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-violet-200 transition-colors cursor-pointer"
            onClick={() => fetchDetail(learner.email, learner.name)}
            data-testid={`elearning-learner-${i}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                <GraduationCap className="h-5 w-5 text-violet-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{learner.name}</p>
                <p className="text-xs text-slate-400">
                  Dernière connexion {learner.last_connection
                    ? `le ${new Date(learner.last_connection).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
                    : 'Jamais connecté'}
                </p>
              </div>
              <div className="flex items-center gap-6">
                {/* Advancement */}
                <div className="min-w-[180px]">
                  <p className="text-xs text-slate-400 mb-1">Avancement</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${Math.min(learner.advancement, 100)}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{Math.round(learner.advancement)} / 100</span>
                  </div>
                </div>
                {/* Realisations */}
                {learner.realisations_received > 0 && (
                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-1">Réalisations reçues</p>
                    <p className="text-xs font-medium text-slate-700">{learner.realisations_received} / {learner.total_activities}</p>
                    {learner.to_correct > 0 && (
                      <p className="text-xs text-amber-600 font-medium mt-0.5">{learner.to_correct} à corriger</p>
                    )}
                  </div>
                )}
                {/* Score */}
                <div className="text-center min-w-[80px]">
                  <p className="text-xs text-slate-400 mb-1">Score</p>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{learner.score > 0 ? `${learner.score} / 100` : '- / 100'}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuiviElearning;
